import type { MaterialSrc } from "common/materials/materialSrc";
import { matchesMaterialCacheKeyFormat, materialSrcFromCacheKey } from "common/materials/materialSrc";
import { logMaterialSrc } from "./logMaterialSrc";

export interface MaterialLoadDiagnosticEntry {
  readonly id: number;
  readonly materialSrc: MaterialSrc;
  readonly hasSSFXRenderer: boolean;
  readonly startTimeMs: number;
  endTimeMs?: number;
  durationMs?: number;
  paramsStartTimeMs?: number;
  paramsDurationMs?: number;
  readyWaitDurationMs?: number;
  designMaterialNewStartTimeMs?: number;
  designMaterialNewDurationMs?: number;
  status?: "fulfilled" | "rejected";
}

export interface DesignMaterialNewDiagnosticEntry {
  readonly id: number;
  readonly materialKey: string;
  readonly startTimeMs: number;
  endTimeMs?: number;
  durationMs?: number;
  setOptionsDurationMs?: number;
  fullyLoadedDurationMs?: number;
  status?: "fulfilled" | "rejected";
}

export type TextureLoadDiagnosticCacheStatus = "miss" | "pending" | "hit";

export interface TextureLoadDiagnosticEntry {
  readonly id: number;
  readonly materialKey: string | null;
  readonly url: string;
  readonly overrideFormat: number | null;
  readonly cacheStatus: TextureLoadDiagnosticCacheStatus;
  readonly startTimeMs: number;
  endTimeMs?: number;
  durationMs?: number;
  status?: "fulfilled" | "rejected";
}

export interface MaterialParamsDiagnosticEntry {
  readonly id: number;
  readonly materialSrc: MaterialSrc;
  readonly startTimeMs: number;
  templateDurationMs?: number;
  layerConfigDurationMs?: number;
  durationMs?: number;
  endTimeMs?: number;
  status?: "fulfilled" | "rejected";
}

export interface MaterialPreloadRunDiagnosticEntry {
  readonly id: number;
  readonly label?: string;
  readonly startTimeMs: number;
  collectDurationMs?: number;
  materialCount?: number;
  materialKeys?: string[];
  endTimeMs?: number;
  durationMs?: number;
  status?: "fulfilled" | "rejected";
}

export interface MaterialLoadDiagnosticsSnapshot {
  readonly enabled: boolean;
  readonly label: string;
  readonly startedAtMs: number;
  readonly materialLoads: MaterialLoadDiagnosticEntry[];
  readonly materialParams: MaterialParamsDiagnosticEntry[];
  readonly designMaterialLoads: DesignMaterialNewDiagnosticEntry[];
  readonly textureLoads: TextureLoadDiagnosticEntry[];
  readonly preloadRuns: MaterialPreloadRunDiagnosticEntry[];
}

export interface MaterialLoadDiagnosticsState {
  enabled: boolean;
  label: string;
  startedAtMs: number;
  materialLoads: MaterialLoadDiagnosticEntry[];
  materialParams: MaterialParamsDiagnosticEntry[];
  designMaterialLoads: DesignMaterialNewDiagnosticEntry[];
  textureLoads: TextureLoadDiagnosticEntry[];
  preloadRuns: MaterialPreloadRunDiagnosticEntry[];
  nextId: number;
}

interface MaterialLoadDiagnosticsGlobal {
  higharcMaterialLoadDiagnostics?: MaterialLoadDiagnosticsState;
}

function diagnosticsGlobal(): MaterialLoadDiagnosticsGlobal {
  return globalThis as unknown as MaterialLoadDiagnosticsGlobal;
}

function nowMs(): number {
  return globalThis.performance?.now?.() ?? Date.now();
}

// Names are derived on demand so entries don't duplicate them. logMaterialSrc (unlike
// pathStringFromMaterialSrc) keeps color overrides on layer-config materials distinguishable.
export function materialNameFromMaterialSrc(materialSrc: MaterialSrc): string {
  return logMaterialSrc(materialSrc);
}

export function materialNameFromMaterialKey(materialKey: string | undefined): string | undefined {
  const cacheKey = materialKey?.split(";")[0];
  if (!cacheKey || !matchesMaterialCacheKeyFormat(cacheKey)) {
    return undefined;
  }

  return materialNameFromMaterialSrc(materialSrcFromCacheKey(cacheKey));
}

function nextDiagnosticId(state: MaterialLoadDiagnosticsState): number {
  state.nextId += 1;
  return state.nextId;
}

export function resetMaterialLoadDiagnostics(label: string): MaterialLoadDiagnosticsSnapshot {
  const state: MaterialLoadDiagnosticsState = {
    enabled: true,
    label,
    startedAtMs: nowMs(),
    nextId: 0,
    materialLoads: [],
    materialParams: [],
    designMaterialLoads: [],
    textureLoads: [],
    preloadRuns: [],
  };
  diagnosticsGlobal().higharcMaterialLoadDiagnostics = state;
  return readMaterialLoadDiagnostics() ?? state;
}

export function readMaterialLoadDiagnostics(): MaterialLoadDiagnosticsSnapshot | null {
  const state = diagnosticsGlobal().higharcMaterialLoadDiagnostics;
  if (!state) {
    return null;
  }

  return {
    enabled: state.enabled,
    label: state.label,
    startedAtMs: state.startedAtMs,
    materialLoads: state.materialLoads.map((entry) => ({ ...entry })),
    materialParams: state.materialParams.map((entry) => ({ ...entry })),
    designMaterialLoads: state.designMaterialLoads.map((entry) => ({ ...entry })),
    textureLoads: state.textureLoads.map((entry) => ({ ...entry })),
    preloadRuns: state.preloadRuns.map((entry) => ({
      ...entry,
      materialKeys: entry.materialKeys ? [...entry.materialKeys] : undefined,
    })),
  };
}

function activeState(): MaterialLoadDiagnosticsState | null {
  const state = diagnosticsGlobal().higharcMaterialLoadDiagnostics;
  return state?.enabled ? state : null;
}

export function startMaterialPreloadRunDiagnostic(label?: string): MaterialPreloadRunDiagnosticEntry | null {
  const state = activeState();
  if (!state) {
    return null;
  }

  const entry: MaterialPreloadRunDiagnosticEntry = {
    id: nextDiagnosticId(state),
    ...(label ? { label } : {}),
    startTimeMs: nowMs(),
  };
  state.preloadRuns.push(entry);
  return entry;
}

export function recordMaterialPreloadCollection(
  diagnostic: MaterialPreloadRunDiagnosticEntry | null,
  materialKeys: string[],
  collectDurationMs: number,
): void {
  if (!diagnostic) {
    return;
  }
  diagnostic.materialCount = materialKeys.length;
  diagnostic.materialKeys = [...materialKeys];
  diagnostic.collectDurationMs = collectDurationMs;
}

export function finishMaterialPreloadRunDiagnostic(
  diagnostic: MaterialPreloadRunDiagnosticEntry | null,
  status: "fulfilled" | "rejected",
): void {
  if (!diagnostic) {
    return;
  }
  const endTimeMs = nowMs();
  diagnostic.endTimeMs = endTimeMs;
  diagnostic.durationMs = endTimeMs - diagnostic.startTimeMs;
  diagnostic.status = status;
}

export function startMaterialLoadDiagnostic({
  hasSSFXRenderer,
  materialSrc,
}: {
  readonly hasSSFXRenderer: boolean;
  readonly materialSrc: MaterialSrc;
}): MaterialLoadDiagnosticEntry | null {
  const state = activeState();
  if (!state) {
    return null;
  }

  const entry: MaterialLoadDiagnosticEntry = {
    id: nextDiagnosticId(state),
    materialSrc,
    hasSSFXRenderer,
    startTimeMs: nowMs(),
  };
  state.materialLoads.push(entry);
  return entry;
}

export function markMaterialLoadParamsStart(diagnostic: MaterialLoadDiagnosticEntry | null): number {
  const startTimeMs = nowMs();
  if (diagnostic) {
    diagnostic.paramsStartTimeMs = startTimeMs;
  }
  return startTimeMs;
}

export function markMaterialLoadParamsEnd(diagnostic: MaterialLoadDiagnosticEntry | null, startTimeMs: number): void {
  if (diagnostic) {
    diagnostic.paramsDurationMs = nowMs() - startTimeMs;
  }
}

export function recordMaterialLoadReadyWait(diagnostic: MaterialLoadDiagnosticEntry | null, durationMs: number): void {
  if (diagnostic) {
    diagnostic.readyWaitDurationMs = durationMs;
  }
}

export function markMaterialLoadDesignMaterialNewStart(diagnostic: MaterialLoadDiagnosticEntry | null): number {
  const startTimeMs = nowMs();
  if (diagnostic) {
    diagnostic.designMaterialNewStartTimeMs = startTimeMs;
  }
  return startTimeMs;
}

export function markMaterialLoadDesignMaterialNewEnd(
  diagnostic: MaterialLoadDiagnosticEntry | null,
  startTimeMs: number,
): void {
  if (diagnostic) {
    diagnostic.designMaterialNewDurationMs = nowMs() - startTimeMs;
  }
}

export function finishMaterialLoadDiagnostic(
  diagnostic: MaterialLoadDiagnosticEntry | null,
  status: "fulfilled" | "rejected",
): void {
  if (!diagnostic) {
    return;
  }
  const endTimeMs = nowMs();
  diagnostic.endTimeMs = endTimeMs;
  diagnostic.durationMs = endTimeMs - diagnostic.startTimeMs;
  diagnostic.status = status;
}

export function startDesignMaterialNewDiagnostic(
  materialKey: string | undefined,
): DesignMaterialNewDiagnosticEntry | null {
  const state = activeState();
  if (!state || !materialKey) {
    return null;
  }

  const entry: DesignMaterialNewDiagnosticEntry = {
    id: nextDiagnosticId(state),
    materialKey,
    startTimeMs: nowMs(),
  };
  state.designMaterialLoads.push(entry);
  return entry;
}

export function recordDesignMaterialSetOptionsDuration(
  diagnostic: DesignMaterialNewDiagnosticEntry | null,
  durationMs: number,
): void {
  if (diagnostic) {
    diagnostic.setOptionsDurationMs = durationMs;
  }
}

export function recordDesignMaterialFullyLoadedDuration(
  diagnostic: DesignMaterialNewDiagnosticEntry | null,
  durationMs: number,
): void {
  if (diagnostic) {
    diagnostic.fullyLoadedDurationMs = durationMs;
  }
}

export function finishDesignMaterialNewDiagnostic(
  diagnostic: DesignMaterialNewDiagnosticEntry | null,
  status: "fulfilled" | "rejected",
): void {
  if (!diagnostic) {
    return;
  }
  const endTimeMs = nowMs();
  diagnostic.endTimeMs = endTimeMs;
  diagnostic.durationMs = endTimeMs - diagnostic.startTimeMs;
  diagnostic.status = status;
}

export function startTextureLoadDiagnostic({
  cacheStatus,
  materialKey,
  overrideFormat,
  url,
}: {
  readonly cacheStatus: TextureLoadDiagnosticCacheStatus;
  readonly materialKey: string | null;
  readonly overrideFormat: number | null;
  readonly url: string;
}): TextureLoadDiagnosticEntry | null {
  const state = activeState();
  if (!state) {
    return null;
  }

  const entry: TextureLoadDiagnosticEntry = {
    id: nextDiagnosticId(state),
    materialKey,
    url,
    overrideFormat,
    cacheStatus,
    startTimeMs: nowMs(),
  };
  state.textureLoads.push(entry);
  return entry;
}

export function startMaterialParamsDiagnostic({
  materialSrc,
}: {
  readonly materialSrc: MaterialSrc;
}): MaterialParamsDiagnosticEntry | null {
  const state = activeState();
  if (!state) {
    return null;
  }

  const entry: MaterialParamsDiagnosticEntry = {
    id: nextDiagnosticId(state),
    materialSrc,
    startTimeMs: nowMs(),
  };
  state.materialParams.push(entry);
  return entry;
}

export function recordMaterialParamsTemplateDuration(
  diagnostic: MaterialParamsDiagnosticEntry | null,
  durationMs: number,
): void {
  if (diagnostic) {
    diagnostic.templateDurationMs = durationMs;
  }
}

export function recordMaterialParamsLayerConfigDuration(
  diagnostic: MaterialParamsDiagnosticEntry | null,
  durationMs: number,
): void {
  if (diagnostic) {
    diagnostic.layerConfigDurationMs = durationMs;
  }
}

export function finishMaterialParamsDiagnostic(
  diagnostic: MaterialParamsDiagnosticEntry | null,
  status: "fulfilled" | "rejected",
): void {
  if (!diagnostic) {
    return;
  }
  const endTimeMs = nowMs();
  diagnostic.endTimeMs = endTimeMs;
  diagnostic.durationMs = endTimeMs - diagnostic.startTimeMs;
  diagnostic.status = status;
}

export function finishTextureLoadDiagnostic(
  diagnostic: TextureLoadDiagnosticEntry | null,
  status: "fulfilled" | "rejected",
): void {
  if (!diagnostic) {
    return;
  }
  const endTimeMs = nowMs();
  diagnostic.endTimeMs = endTimeMs;
  diagnostic.durationMs = endTimeMs - diagnostic.startTimeMs;
  diagnostic.status = status;
}
